import { apiClient } from "@/api"
import WeekCalendar from "@/components/calendar/week/WeekCalendar"
import { Button, PageTitle } from "@/library"
import BRTThemeProvider from "@/providers/theme"
import { Alert, Box, CircularProgress, Paper, useMediaQuery, useTheme } from "@mui/material"
import { Container, styled } from "@mui/system"
import { endOfMonth, endOfWeek, format, parseISO, startOfMonth, startOfWeek } from "date-fns"
import React, { useCallback, useEffect, useReducer, useRef } from "react"
import CalendarFilters from "../CalendarFilters"
import CalendarNavigation from "../CalendarNavigation"
import { CALENDAR_DIMENSIONS, DEFAULTS, ENDPOINTS, ERROR_MESSAGES, VIEW_MODES } from "../constants"
import { useCalendarFilters } from "../hooks/useCalendarFilters"
import LegendWrapper from "../legends/legend"

const CalendarWrapper = styled(Paper)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  padding: "2rem",
}))

const CalendarContent = styled(Box)({
  flexGrow: 1,
  overflow: "auto",
  border: `1px solid #e6e4f1`,
  marginTop: "1rem",
})

const initialState = {
  events: [],
  isParent: false,
  isLoading: false,
  error: null,
}

const reducer = (state, action) => {
  switch (action.type) {
    case "FETCH_INIT":
      return { ...state, isLoading: true, error: null }
    case "FETCH_SUCCESS":
      return { ...state, events: action.payload.events, isParent: action.payload.isParent, isLoading: false }
    case "FETCH_FAILURE":
      return { ...state, error: action.payload, isLoading: false }
    case "RESET_CALENDAR":
      return { ...state, events: [], isParent: false, isLoading: false }
    default:
      throw new Error("Invalid action type")
  }
}

const AdminWeeklyCalendar = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [state, dispatch] = useReducer(reducer, initialState)

  // Destructure filter props from our improved hook.
  // Renaming isLoading to isFiltersLoading for clarity.
  const {
    selectedValues,
    currentDate,
    viewMode,
    handleFilterChange,
    handleResetFilters,
    handleTypeChange,
    renderAutocomplete,
    activeFilters,
    needsFilterWarning,
    handleNavigateToday,
    handleNavigatePrev,
    handleNavigateNext,
    handleViewChange,
    isLoading: isFiltersLoading,
  } = useCalendarFilters()

  // Derive a common flag to check if tutor or school is selected.
  const hasFilters = Boolean(selectedValues?.tutor?.value || selectedValues?.school?.value)

  const handleResetCalendar = useCallback(() => {
    dispatch({ type: "RESET_CALENDAR" })
    handleResetFilters()
  }, [handleResetFilters, dispatch])

  // Consolidate date range generation.
  const getDateRange = useCallback(() => {
    const dateObj = parseISO(currentDate)
    if (viewMode === VIEW_MODES.WEEK) {
      const start = startOfWeek(dateObj, { weekStartsOn: 0 })
      const end = endOfWeek(dateObj, { weekStartsOn: 0 })
      return {
        start_date: format(start, DEFAULTS.DATE_FORMAT),
        end_date: format(end, DEFAULTS.DATE_FORMAT),
      }
    } else {
      const start = startOfMonth(dateObj)
      const end = endOfMonth(dateObj)
      return {
        start_date: format(start, DEFAULTS.DATE_FORMAT),
        end_date: format(end, DEFAULTS.DATE_FORMAT),
      }
    }
  }, [currentDate, viewMode])

  // Centralize API call logic.
  const fetchCalendarEvents = useCallback(async () => {
    const queryParams = { ...getDateRange() }

    if (selectedValues?.tutor?.value) {
      queryParams.tutor_id = selectedValues.tutor.value
    }
    if (selectedValues?.school?.value) {
      queryParams.school_id = selectedValues.school.value
    }
    if (selectedValues?.group?.value) {
      queryParams.group_id = selectedValues.group.value
    }

    const endpoint = selectedValues?.type || DEFAULTS.SESSION_TYPE
    const searchParams = new URLSearchParams(queryParams).toString()

    try {
      const { data } = await apiClient.get(`${ENDPOINTS.calendar[endpoint]}?${searchParams}`, {
        headers: { Accept: "application/json" },
      })
      dispatch({
        type: "FETCH_SUCCESS",
        payload: { events: data.events || [], isParent: data.isParent || false },
      })
    } catch (error) {
      dispatch({
        type: "FETCH_FAILURE",
        payload: error.response?.data?.error || error.message || ERROR_MESSAGES.FETCH_CALENDAR_ERROR,
      })
    }
  }, [selectedValues, getDateRange])

  // Fetch calendar data when filters or dates change.
  useEffect(() => {
    if (isFiltersLoading) return

    dispatch({ type: "FETCH_INIT" })
    fetchCalendarEvents()
  }, [selectedValues, viewMode, currentDate, isFiltersLoading, fetchCalendarEvents])

  const handleScheduleFamily = useCallback(() => {
    window.location.href = "/admin/sessions/new"
  }, [])

  // Used for displaying alerts in the UI.
  const isEmptyMonthView = viewMode === VIEW_MODES.MONTH && !hasFilters

  // Manage scroll position to avoid jumps between data loads.
  const calendarContentRef = useRef(null)
  const scrollPositionRef = useRef(0)

  useEffect(() => {
    if (calendarContentRef.current) {
      scrollPositionRef.current = calendarContentRef.current.scrollTop
    }
  }, [selectedValues, viewMode, currentDate])

  useEffect(() => {
    if (calendarContentRef.current && !state.isLoading) {
      calendarContentRef.current.scrollTop = scrollPositionRef.current
    }
  }, [state.events, state.isLoading])

  return (
    <BRTThemeProvider>
      <Container maxWidth={false} sx={{ mb: 4 }}>
        <Box sx={{ padding: "1rem 3.5rem" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <PageTitle variant={isMobile ? "h5" : "h2"} sx={{ paddingLeft: "1rem", textAlign: "left" }}>
              Calendar
            </PageTitle>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleScheduleFamily}
              aria-label="Schedule a family session"
            >
              Schedule Family Session
            </Button>
          </Box>
          <CalendarWrapper elevation={3} ref={calendarContentRef}>
            <CalendarFilters
              selectedValues={selectedValues || {}}
              handleResetFilters={handleResetCalendar}
              handleTypeChange={handleTypeChange}
              renderAutocomplete={renderAutocomplete}
              activeFilters={activeFilters}
              needsFilterWarning={needsFilterWarning}
              onFilterChange={handleFilterChange}
            />
            <CalendarNavigation
              onNavigateToday={handleNavigateToday}
              onNavigatePrev={handleNavigatePrev}
              onNavigateNext={handleNavigateNext}
              showViewToggle
              currentView={viewMode}
              onViewChange={handleViewChange}
              currentDate={currentDate}
              isLoading={state.isLoading}
            />
            <CalendarContent>
              {state.isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: CALENDAR_DIMENSIONS.DEFAULT_HEIGHT,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : state.error ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="error">{state.error}</Alert>
                </Box>
              ) : isEmptyMonthView ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: CALENDAR_DIMENSIONS.DEFAULT_HEIGHT,
                  }}
                >
                  <Alert severity="info" sx={{ maxWidth: "80%" }}>
                    {ERROR_MESSAGES.MONTH_VIEW_FILTER}
                  </Alert>
                </Box>
              ) : state.events.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    pt: { xs: 8, md: 16, lg: 20 },
                    height: CALENDAR_DIMENSIONS.DEFAULT_HEIGHT,
                  }}
                >
                  <Alert severity="info" sx={{ maxWidth: "80%" }}>
                    {hasFilters
                      ? "There are no available sessions this week matching your selected filters."
                      : "Please select a tutor or school above to view sessions."}
                  </Alert>
                </Box>
              ) : (
                <WeekCalendar
                  calendarData={{
                    events: state.events,
                    weekStart: currentDate,
                    isParent: state.isParent,
                    viewMode,
                  }}
                  month={currentDate}
                  filters={selectedValues || {}}
                  isMobile={isMobile}
                  viewMode={viewMode}
                />
              )}
            </CalendarContent>
            <LegendWrapper />
          </CalendarWrapper>
        </Box>
      </Container>
    </BRTThemeProvider>
  )
}

export default AdminWeeklyCalendar
